import { BOOKING_STATUS_LABEL, clock, formatRupees, indiaNow, WEEKDAYS } from '@quickreport/domain';
import { Badge, Button, Field, Input, Modal, Textarea } from '@quickreport/ui';
import clsx from 'clsx';
import {
  CalendarDays,
  Check,
  ChevronLeft,
  ChevronRight,
  Clock,
  Home,
  MapPin,
  Phone,
  RefreshCw,
  X,
} from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { describeError } from '../../lib/api';
import { type Booking, bookingsApi, bookingsChanged, type CalendarDay } from '../../lib/bookings';

const monthOf = (date: string) => date.slice(0, 7);
function shiftMonth(month: string, by: number): string {
  const d = new Date(`${month}-01T00:00:00Z`);
  d.setUTCMonth(d.getUTCMonth() + by);
  return d.toISOString().slice(0, 7);
}
const monthName = (month: string) =>
  new Date(`${month}-01T00:00:00Z`).toLocaleString('en-IN', {
    month: 'long',
    year: 'numeric',
    timeZone: 'UTC',
  });
const dayName = (date: string) =>
  new Date(`${date}T00:00:00Z`).toLocaleString('en-IN', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    timeZone: 'UTC',
  });

/** The days of a month laid out Monday first, with blanks before the 1st. */
function monthGrid(month: string): (string | null)[] {
  const first = new Date(`${month}-01T00:00:00Z`);
  const lead = (first.getUTCDay() + 6) % 7;
  const days = new Date(Date.UTC(first.getUTCFullYear(), first.getUTCMonth() + 1, 0)).getUTCDate();
  const cells: (string | null)[] = Array(lead).fill(null);
  for (let d = 1; d <= days; d++) cells.push(`${month}-${String(d).padStart(2, '0')}`);
  while (cells.length % 7) cells.push(null);
  return cells;
}

/** A day's bookings by time, in order; several people often book the same time. */
function byTime(list: Booking[]): [string, Booking[]][] {
  const m = new Map<string, Booking[]>();
  for (const b of list) m.set(b.time, [...(m.get(b.time) ?? []), b]);
  return [...m.entries()].sort((a, b) => a[0].localeCompare(b[0]));
}

const statusTone = (s: Booking['status']) =>
  s === 'pending' ? 'warn' : s === 'approved' ? 'ok' : s === 'done' ? 'brand' : 'neutral';

/** Bookings people made through the lab's public page: a month calendar and each day's list. */
export function BookingsPage() {
  const today = indiaNow().date;
  const [month, setMonth] = useState(monthOf(today));
  const [day, setDay] = useState(today);
  const [calendar, setCalendar] = useState<Map<string, CalendarDay>>(new Map());
  const [list, setList] = useState<Booking[] | null>(null);
  const [waiting, setWaiting] = useState<Booking[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [deciding, setDeciding] = useState<{ b: Booking; approve: boolean } | null>(null);

  const load = useCallback(async () => {
    try {
      const [cal, dayList, pending] = await Promise.all([
        bookingsApi.calendar(month),
        bookingsApi.list({ from: day, to: day }),
        bookingsApi.list({ status: 'pending', from: today }),
      ]);
      setCalendar(new Map(cal.days.map((d) => [d.date, d])));
      setList(dayList.items);
      setWaiting(pending.items);
      setError(null);
    } catch (err) {
      setError(describeError(err));
      setList((l) => l ?? []);
    }
  }, [month, day, today]);

  useEffect(() => {
    void load();
    const t = setInterval(() => void load(), 60_000);
    return () => clearInterval(t);
  }, [load]);

  async function markDone(b: Booking) {
    try {
      await bookingsApi.done(b.id);
      bookingsChanged();
      await load();
    } catch (err) {
      setError(describeError(err));
    }
  }

  /** Several people booked the same time: confirm them together. */
  async function approveAll(group: Booking[]) {
    try {
      for (const b of group) await bookingsApi.decide(b.id, { approve: true, comment: '' });
      bookingsChanged();
      await load();
    } catch (err) {
      setError(describeError(err));
      await load();
    }
  }

  const cells = useMemo(() => monthGrid(month), [month]);

  return (
    <div className="flex h-full flex-col">
      <header className="flex flex-wrap items-center gap-3 border-b border-line bg-paper px-4 py-3 md:px-6">
        <div className="min-w-0 flex-1">
          <h1 className="text-xl font-semibold">Bookings</h1>
          <p className="text-slate max-md:hidden">
            Visits and home collections people booked from your public page. Approve or decline each
            one; they are told on their phone straight away.
          </p>
        </div>
        <Button onClick={() => void load()} aria-label="Refresh">
          <RefreshCw size={15} /> Refresh
        </Button>
      </header>
      {error ? (
        <div
          role="alert"
          className="border-b border-red-200 bg-red-50 px-4 py-2 text-[13px] text-flag md:px-6"
        >
          {error}
        </div>
      ) : null}

      <div className="min-h-0 flex-1 overflow-auto p-4 md:p-6">
        <div className="grid gap-6 lg:grid-cols-[minmax(0,26rem)_minmax(0,1fr)]">
          <div className="grid content-start gap-6">
            <section aria-label="Calendar" className="rounded-xl border border-line bg-paper p-3">
              <div className="mb-2 flex items-center gap-2">
                <button
                  type="button"
                  aria-label="Previous month"
                  onClick={() => setMonth((m) => shiftMonth(m, -1))}
                  className="grid h-8 w-8 place-items-center rounded-lg hover:bg-canvas"
                >
                  <ChevronLeft size={18} />
                </button>
                <h2 className="flex-1 text-center font-semibold">{monthName(month)}</h2>
                <button
                  type="button"
                  aria-label="Next month"
                  onClick={() => setMonth((m) => shiftMonth(m, 1))}
                  className="grid h-8 w-8 place-items-center rounded-lg hover:bg-canvas"
                >
                  <ChevronRight size={18} />
                </button>
              </div>
              <div className="grid grid-cols-7 gap-1 text-center text-[11px] font-medium text-slate">
                {WEEKDAYS.map((w) => (
                  <div key={w}>{w}</div>
                ))}
              </div>
              <div className="mt-1 grid grid-cols-7 gap-1">
                {cells.map((date, i) => {
                  // biome-ignore lint/suspicious/noArrayIndexKey: empty cells have nothing else to tell them apart
                  if (!date) return <div key={`blank-${i}`} />;
                  const c = calendar.get(date);
                  const chosen = date === day;
                  return (
                    <button
                      key={date}
                      type="button"
                      onClick={() => {
                        if (date !== day) setList(null);
                        setDay(date);
                      }}
                      aria-pressed={chosen}
                      aria-label={`${dayName(date)}${c ? `, ${c.total} booking${c.total === 1 ? '' : 's'}${c.pending ? `, ${c.pending} waiting` : ''}` : ''}`}
                      className={clsx(
                        'relative flex h-14 flex-col items-center justify-start rounded-lg pt-1.5 text-[13px] transition-colors',
                        chosen ? 'bg-navy text-white' : 'hover:bg-canvas',
                        date === today && !chosen && 'ring-1 ring-brand',
                        date < today && !chosen && 'text-slate',
                      )}
                    >
                      <span className="font-medium">{Number(date.slice(8))}</span>
                      {c && c.total > 0 ? (
                        <span className="mt-1 flex gap-0.5">
                          {c.pending > 0 ? (
                            <span
                              className={clsx(
                                'rounded px-1 text-[10px] font-semibold',
                                chosen ? 'bg-white/20' : 'bg-amber-100 text-warn',
                              )}
                            >
                              {c.pending}
                            </span>
                          ) : null}
                          {c.approved > 0 ? (
                            <span
                              className={clsx(
                                'rounded px-1 text-[10px] font-semibold',
                                chosen ? 'bg-white/20' : 'bg-green-100 text-ok',
                              )}
                            >
                              {c.approved}
                            </span>
                          ) : null}
                        </span>
                      ) : null}
                      {c && c.home > 0 ? (
                        <Home size={10} className="absolute top-1 right-1 opacity-70" aria-hidden />
                      ) : null}
                    </button>
                  );
                })}
              </div>
              <p className="mt-3 flex flex-wrap gap-3 text-[11px] text-slate">
                <span className="flex items-center gap-1">
                  <span className="h-2.5 w-2.5 rounded bg-amber-100 ring-1 ring-amber-300" />{' '}
                  Waiting
                </span>
                <span className="flex items-center gap-1">
                  <span className="h-2.5 w-2.5 rounded bg-green-100 ring-1 ring-green-300" />{' '}
                  Confirmed
                </span>
                <span className="flex items-center gap-1">
                  <Home size={11} /> Home collection
                </span>
              </p>
            </section>

            <section aria-label="Waiting for your answer">
              <h2 className="mb-2 flex items-center gap-2 font-semibold">
                Waiting for your answer{' '}
                {waiting.length ? <Badge tone="warn">{waiting.length}</Badge> : null}
              </h2>
              {waiting.length === 0 ? (
                <p className="rounded-lg border border-dashed border-line p-4 text-center text-[13px] text-slate">
                  Nothing waiting. New bookings appear here and on your phone.
                </p>
              ) : (
                <ul className="grid gap-2">
                  {waiting.map((b) => (
                    <li key={b.id}>
                      <button
                        type="button"
                        onClick={() => {
                          setMonth(monthOf(b.date));
                          if (b.date !== day) setList(null);
                          setDay(b.date);
                        }}
                        className="flex w-full items-center gap-3 rounded-lg border border-line bg-paper p-2.5 text-left hover:border-brand"
                      >
                        <span className="grid h-10 w-10 shrink-0 place-items-center rounded-lg bg-amber-50 text-warn">
                          {b.kind === 'home' ? <Home size={18} /> : <CalendarDays size={18} />}
                        </span>
                        <span className="min-w-0 flex-1">
                          <span className="block truncate font-medium">{b.patientName}</span>
                          <span className="block truncate text-[12px] text-slate">
                            {dayName(b.date)}, {clock(b.time)} · {b.tests.length} test
                            {b.tests.length === 1 ? '' : 's'}
                          </span>
                        </span>
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </section>
          </div>

          <section aria-label={`Bookings on ${dayName(day)}`} className="min-w-0">
            <h2 className="mb-3 text-lg font-semibold">{dayName(day)}</h2>
            {list === null ? (
              <p className="text-slate">Loading…</p>
            ) : list.length === 0 ? (
              <p className="rounded-lg border border-dashed border-line p-8 text-center text-slate">
                No bookings on this day.
              </p>
            ) : (
              <div className="grid gap-5">
                {byTime(list).map(([time, group]) => {
                  const waitingHere = group.filter((b) => b.status === 'pending');
                  const live = group.filter(
                    (b) => b.status !== 'declined' && b.status !== 'cancelled',
                  );
                  return (
                    <section key={time} aria-label={`${clock(time)}, ${live.length} booked`}>
                      <div className="mb-2 flex flex-wrap items-center gap-2">
                        <h3 className="font-semibold">{clock(time)}</h3>
                        <span className="text-[13px] text-slate">
                          {live.length} booked
                          {live.some((b) => b.kind === 'home')
                            ? ` · ${live.filter((b) => b.kind === 'home').length} at home`
                            : ''}
                        </span>
                        {waitingHere.length > 1 ? (
                          <Button
                            size="sm"
                            className="ml-auto"
                            onClick={() => void approveAll(waitingHere)}
                          >
                            <Check size={13} /> Approve all {waitingHere.length}
                          </Button>
                        ) : null}
                      </div>
                      <ul className="grid gap-3">
                        {group.map((b) => (
                          <BookingCard
                            key={b.id}
                            b={b}
                            onApprove={() => setDeciding({ b, approve: true })}
                            onDecline={() => setDeciding({ b, approve: false })}
                            onDone={() => void markDone(b)}
                          />
                        ))}
                      </ul>
                    </section>
                  );
                })}
              </div>
            )}
          </section>
        </div>
      </div>

      {deciding ? (
        <DecideDialog
          booking={deciding.b}
          approve={deciding.approve}
          onClose={() => setDeciding(null)}
          onDone={async () => {
            setDeciding(null);
            bookingsChanged();
            await load();
          }}
        />
      ) : null}
    </div>
  );
}

function BookingCard({
  b,
  onApprove,
  onDecline,
  onDone,
}: {
  b: Booking;
  onApprove: () => void;
  onDecline: () => void;
  onDone: () => void;
}) {
  const past = b.date < indiaNow().date;
  return (
    <li
      className={clsx(
        'rounded-xl border bg-paper p-4',
        b.status === 'pending' ? 'border-amber-300' : 'border-line',
      )}
    >
      <div className="flex flex-wrap items-start gap-3">
        <div className="grid w-16 shrink-0 place-items-center rounded-lg bg-canvas py-2 text-center">
          <Clock size={14} className="text-slate" aria-hidden />
          <span className="text-[13px] font-semibold">{clock(b.time)}</span>
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <span className="font-semibold">{b.patientName}</span>
            <span className="text-[13px] text-slate">
              {[b.patientAge && `${b.patientAge} y`, b.patientSex].filter(Boolean).join(' · ')}
            </span>
            <Badge tone={statusTone(b.status)}>{BOOKING_STATUS_LABEL[b.status]}</Badge>
            {b.kind === 'home' ? (
              <Badge tone="brand">
                <Home size={11} className="mr-1" /> Home collection
              </Badge>
            ) : (
              <Badge>At the lab</Badge>
            )}
            {b.reportDelivery ? <Badge>Report delivery</Badge> : null}
          </div>
          <div className="mt-1 text-[13px]">{b.tests.map((t) => t.name).join(', ')}</div>
          <div className="mt-1 flex flex-wrap gap-x-4 gap-y-1 text-[13px] text-slate">
            <a
              href={`tel:${b.contact}`}
              className="flex items-center gap-1 text-navy hover:underline"
            >
              <Phone size={13} /> {b.contact}
            </a>
            <span>
              {formatRupees(b.totalPaise)}
              {b.feePaise ? ` (incl. ${formatRupees(b.feePaise)} fee)` : ''}
            </span>
            <span>Code {b.code}</span>
          </div>
          {b.address ? (
            <p className="mt-1 flex items-start gap-1 text-[13px]">
              <MapPin size={13} className="mt-0.5 shrink-0 text-slate" /> {b.address}
            </p>
          ) : null}
          {b.note ? <p className="mt-1 text-[13px] italic text-slate">“{b.note}”</p> : null}
          {b.labComment ? <p className="mt-1 text-[13px]">Your note: {b.labComment}</p> : null}
          {b.cancelReason ? (
            <p className="mt-1 text-[13px] text-flag">Cancelled: {b.cancelReason}</p>
          ) : null}
        </div>
      </div>
      {b.status === 'pending' || b.status === 'approved' ? (
        <div className="mt-3 flex flex-wrap justify-end gap-2 border-t border-line pt-3">
          {b.status === 'pending' ? (
            <>
              <Button tone="danger" onClick={onDecline}>
                <X size={15} /> Decline
              </Button>
              <Button tone="primary" onClick={onApprove}>
                <Check size={15} /> Approve
              </Button>
            </>
          ) : (
            <>
              <Button tone="danger" onClick={onDecline}>
                Cancel booking
              </Button>
              {past ? null : <Button onClick={onApprove}>Change time</Button>}
              {/* Done once the day has come: the sample was taken. */}
              {b.date <= indiaNow().date ? (
                <Button tone="primary" onClick={onDone}>
                  <Check size={15} /> Mark done
                </Button>
              ) : null}
            </>
          )}
        </div>
      ) : null}
    </li>
  );
}

const DECLINE_REASONS = [
  'Fully booked at that time',
  'This test is not available on that day',
  'The address is outside our home collection area',
  'Please call the lab to book',
];

function DecideDialog({
  booking,
  approve,
  onClose,
  onDone,
}: {
  booking: Booking;
  approve: boolean;
  onClose: () => void;
  onDone: () => Promise<void>;
}) {
  const [comment, setComment] = useState('');
  const [date, setDate] = useState(booking.date);
  const [time, setTime] = useState(booking.time);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const moved = date !== booking.date || time !== booking.time;

  async function submit() {
    if (!approve && !comment.trim()) {
      setError('Say why, so the person can book again.');
      return;
    }
    setBusy(true);
    try {
      await bookingsApi.decide(booking.id, {
        approve,
        comment: comment.trim(),
        ...(approve && moved ? { date, time } : {}),
      });
      await onDone();
    } catch (err) {
      setError(describeError(err));
      setBusy(false);
    }
  }

  return (
    <Modal
      title={
        approve
          ? booking.status === 'approved'
            ? 'Change the time'
            : 'Approve booking'
          : booking.status === 'approved'
            ? 'Cancel booking'
            : 'Decline booking'
      }
      label="Answer the booking"
      onClose={onClose}
    >
      <div className="grid gap-4">
        <p className="text-[13px] text-slate">
          {booking.patientName} · {booking.tests.map((t) => t.name).join(', ')} ·{' '}
          {dayName(booking.date)}, {clock(booking.time)}
        </p>
        {approve ? (
          <div className="grid grid-cols-2 gap-3">
            <Field label="Date">
              <Input
                type="date"
                value={date}
                min={indiaNow().date}
                onChange={(e) => setDate(e.target.value)}
              />
            </Field>
            <Field label="Time">
              <Input
                type="time"
                value={time}
                step={300}
                onChange={(e) => setTime(e.target.value)}
              />
            </Field>
            {moved ? (
              <p className="col-span-2 rounded-md bg-amber-50 px-3 py-2 text-[12px] text-warn">
                The person is told the new time. Say why in the note.
              </p>
            ) : null}
          </div>
        ) : (
          <div className="flex flex-wrap gap-1.5">
            {DECLINE_REASONS.map((r) => (
              <button
                key={r}
                type="button"
                onClick={() => setComment(r)}
                className="rounded-full border border-line px-2.5 py-1 text-[12px] hover:border-brand"
              >
                {r}
              </button>
            ))}
          </div>
        )}
        <Field
          label={approve ? 'Note for the person (optional)' : 'Reason (sent to the person)'}
          error={error}
        >
          <Textarea
            rows={3}
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            placeholder={
              approve
                ? 'e.g. Come fasting for 10 hours. Our collector will call before coming.'
                : ''
            }
          />
        </Field>
        <div className="flex justify-end gap-2">
          <Button onClick={onClose}>Back</Button>
          <Button
            tone={approve ? 'primary' : 'danger'}
            disabled={busy}
            onClick={() => void submit()}
          >
            {approve
              ? booking.status === 'approved'
                ? 'Save new time'
                : 'Approve'
              : booking.status === 'approved'
                ? 'Cancel booking'
                : 'Decline'}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
